import {
  TimetablePrintColorOrigins,
  TimetablePrintColorPositions,
  TimetablePrintLayoutOptions,
  TimetablePrintPersonNameFormats,
  TimetableReportType,
} from '@bp/planung-graphql-types';
import { Grid, GridRow, ModalBottomButtons } from '@bp/ui-components';
import { Formik } from 'formik';
import { t } from 'i18next';
import { useEffect } from 'react';
import { Form } from 'react-router-dom';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  use_SaveTimetablePrintConfigMutation,
  use_TimetablePrintConfigQuery,
} from '../../../types/planung-graphql-client-defs';
import { showErrorToast, showSuccessUpdateToast } from '../../../utils/toast';
import { TimetablePrintConfigurationBaseData } from '../components/TimetablePrintConfigurationBaseData';
import { TimetablePrintConfigurationGrid } from '../components/TimetablePrintConfigurationGrid';
import { useGetInitialTimetablePrintConfig } from '../useGetTimetablePrintConfig';

export type TimetablePrintBaseData = {
  color: string;
  colorPosition: TimetablePrintColorPositions;
  colorOrigin: TimetablePrintColorOrigins;
  printClasses: boolean;
  scaleText: boolean;
  exactMinute: boolean;
  printSubtitle: boolean;
  printLogo: boolean;
  largeFont: boolean;
  showSchoolWeeks: boolean;
  showCalendarWeeks: boolean;
  showWeekSchoolDays: boolean;
  timeHorizontal: boolean;
  epochGridOwnPage: boolean;
  responsiblePersonNameFormat: TimetablePrintPersonNameFormats;
};

export enum TimetablePrintConfigurationOptions {
  printClasses = 'printClasses',
  scaleText = 'scaleText',
  exactMinute = 'exactMinute',
  printSubtitle = 'printSubtitle',
  printLogo = 'printLogo',
  largeFont = 'largeFont',
  showSchoolWeeks = 'showSchoolWeeks',
  showCalendarWeeks = 'showCalendarWeeks',
  showWeekSchoolDays = 'showWeekSchoolDays',
  timeHorizonal = 'timeHorizontal',
  epochGridOwnPage = 'epochGridOwnPage',
}

/** Report-Typen, bei denen der Epochenplan zusammen mit einem Stundenplan auf einer Seite steht. */
export const REPORT_TYPES_WITH_COMBINED_EPOCH_GRID: TimetableReportType[] = [
  TimetableReportType.SingleClass,
  TimetableReportType.SingleTeacher,
  TimetableReportType.SingleRoom,
];

export enum TimetablePrintLayoutPositions {
  topLeft = 'topLeft',
  topCenter = 'topCenter',
  topRight = 'topRight',
  center = 'center',
  bottomLeft = 'bottomLeft',
  bottomCenter = 'bottomCenter',
  bottomRight = 'bottomRight',
}

export type TimetablePrintLayoutData = {
  topLeft: TimetablePrintLayoutOptions;
  topCenter: TimetablePrintLayoutOptions;
  topRight: TimetablePrintLayoutOptions;
  center: TimetablePrintLayoutOptions;
  bottomLeft: TimetablePrintLayoutOptions;
  bottomCenter: TimetablePrintLayoutOptions;
  bottomRight: TimetablePrintLayoutOptions;
};

export type TimetablePrintConfigurationFormType = TimetablePrintBaseData & TimetablePrintLayoutData;

type TimetablePrintConfigurationFormProps = {
  subType: TimetableReportType;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const TimetablePrintConfigurationForm = ({ subType, onLoad, onClose }: TimetablePrintConfigurationFormProps) => {
  const context = useMemorizedCacheTag('TIMETABLE_PRINT_CONFIG');

  const [, saveConfig] = use_SaveTimetablePrintConfigMutation();

  const [{ data, error }] = use_TimetablePrintConfigQuery({
    variables: { type: subType },
    context,
    pause: !subType,
  });

  const current = data?.timetablePrintConfig;
  // Formik übernimmt nur die ersten Startwerte: erst rendern, wenn die gespeicherte Einstellung da ist
  const loaded = data !== undefined || error !== undefined;

  useEffect(() => {
    onLoad(!loaded);
  }, [loaded, onLoad]);

  const { initialBaseData, initialLayoutData } = useGetInitialTimetablePrintConfig({ subType, current });

  const initialValues: TimetablePrintConfigurationFormType = { ...initialBaseData, ...initialLayoutData };

  async function handleSubmit(values: TimetablePrintConfigurationFormType) {
    onLoad(true);

    const input = {
      printClasses: values?.printClasses,
      scaleText: values?.scaleText,
      exactMinute: values?.exactMinute,
      printSubtitle: values?.printSubtitle,
      printLogo: values?.printLogo,
      largeFont: values?.largeFont,
      printColorOrigin: values?.colorOrigin,
      printColorPosition: values?.colorPosition,
      showSchoolWeeks: values?.showSchoolWeeks,
      showCalendarWeeks: values?.showCalendarWeeks,
      showWeekSchoolDays: values?.showWeekSchoolDays,
      timeHorizontal: values?.timeHorizontal,
      epochGridOwnPage: values?.epochGridOwnPage,
      responsiblePersonNameFormat: values?.responsiblePersonNameFormat,
      bottomCenter: values?.bottomCenter,
      bottomLeft: values?.bottomLeft,
      bottomRight: values?.bottomRight,
      center: values?.center,
      topLeft: values?.topLeft,
      topCenter: values?.topCenter,
      topRight: values?.topRight,
    };

    // Je Berichtstyp gibt es höchstens eine Einstellung; das Backend legt sie an oder ersetzt sie.
    const result = await saveConfig({ type: subType, input }, context);

    if (result.error) {
      showErrorToast(result.error);
    } else {
      showSuccessUpdateToast();
    }

    onLoad(false);
    onClose();
  }

  if (!loaded) {
    return null;
  }

  return (
    <Formik<TimetablePrintConfigurationFormType> initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, resetForm }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingTop='none' spacingBottom='l'>
                <TimetablePrintConfigurationBaseData subType={subType} />
              </GridRow>
              <GridRow headline={t('timetable.configuration.positions')}>
                <TimetablePrintConfigurationGrid />
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{
                text: t('common.save'),
                callback: async () => {
                  await handleSubmit(values);
                  resetForm();
                },
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
